import React, { useEffect, useState } from 'react';
import { DateRangePicker } from 'rsuite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays, faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import isPeriodRangeEqual from '../../../services/periods';
import './DateRangePicker.scss';

const today = new Date();
today.setHours(0, 0, 0, 0);

/** @type {import('rsuite/cjs/DateRangePicker/types').RangeType} */
const defaultValue = { label: '', value: [] };

/**
 * This component is for RSuite v5
 * @param {import('rsuite/DateRangePicker').DateRangePickerProps & {
 *  selectedPeriod?: import('rsuite/cjs/DateRangePicker/types').RangeType
 *  isOpen?: boolean,
 *  onClose?: () => void,
 *  hasError?: boolean,
 * }} props
 * @returns
 */
const DateRangePickerWrapper = ({
    selectedPeriod,
    onChange,
    size,
    ranges,
    hasError,
    character,
    value: controlledValue,
    isOpen,
    onClose,
    disabled,
    ...others
}) => {
    const [isInitialized, setInitialized] = useState(false);
    const [selectedRange, setSelectedRange] = useState(defaultValue);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    useEffect(() => {
        if (isInitialized) {
            return;
        }

        if (selectedPeriod?.value) {
            setSelectedRange(selectedPeriod);
            setInitialized(true);
        }
    });

    const onSelectionChange = (
        /** @type {import('rsuite/cjs/DateRangePicker/types').ValueType} */ value,
        /** @type {React.SyntheticEvent<Element, Event>} */ event,
    ) => {
        const updatedValue = { ...defaultValue, value };
        if (!value?.length || !ranges?.length) {
            setSelectedRange(defaultValue);
        } else {
            // @ts-ignore
            const updatedSelectedRange = ranges?.find((range) => isPeriodRangeEqual(range.value, value));
            setSelectedRange(updatedSelectedRange || updatedValue);
        }

        if (onChange) {
            onChange(value, event);
        }
    };

    const handleOpenMenu = () => {
        if (disabled) return;
        setIsMenuOpen(!isMenuOpen);
    }

    const calendarIcon = React.forwardRef((iconProps, ref) => {
        return (
            <FontAwesomeIcon
                className="date-time-picker-caret-icon"
                icon={faCalendarDays} 
                {...iconProps} 
                ref={ref}
            />)
    });

    const chevronIcon = isMenuOpen ? faChevronUp : faChevronDown;
    const open = isOpen ?? isMenuOpen;
    
    return (
        <div className={`core--date-range-picker-wrapper ${hasError ? 'core--date-range-picker-wrapper-error' : ''}`}>
            <div className={`selected-label ${selectedRange?.label ? '' : 'hidden'}`}>
                <span>{selectedRange?.label}</span>
            </div>
            <DateRangePicker
                menuClassName={ranges?.length ? 'core--date-range-picker-menu-with-ranges' : 'core--date-range-picker-menu'}
                ranges={ranges}
                size={size || 'lg'}
                caretAs={calendarIcon}
                character={character || ' - '}
                onChange={onSelectionChange}
                onOpen={() => setIsMenuOpen(true)}
                onOk={() => setIsMenuOpen(false)}
                onClose={() => setIsMenuOpen(false)}
                open={open}
                value={controlledValue?.filter((date) => date !== null)}
                disabled={disabled}
                {...others}
            />
            <div
                className="date-range-picker-chevron-icon-wrapper"
                onClick={handleOpenMenu}
            >
                <FontAwesomeIcon icon={chevronIcon} />
            </div>
            {open && (
                <div
                    className="core--date-range-picker-overlay"
                    onClick={() => onClose ? onClose() : setIsMenuOpen(false)}
                >
                </div>
            )}
        </div>
    );
};

export default DateRangePickerWrapper;
